import React, { useEffect, useState, useCallback } from "react";
import PageShell from "@/components/PageShell";
import { api } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import Avatar from "@/components/Avatar";
import RoleBadge from "@/components/RoleBadge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, Sparkles, ChevronLeft, ChevronRight } from "lucide-react";
import { formatRp } from "@/lib/tiers";

const PER_PAGE = 24;

export default function SupportersPage() {
  const { settings } = useSettings();
  const [q, setQ] = useState("");
  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [disabled, setDisabled] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/supporters", {
        params: { q, limit: PER_PAGE, skip: (page - 1) * PER_PAGE },
      });
      setList(data.supporters || []);
      setTotal(data.total || 0);
      setDisabled(false);
    } catch (e) {
      if (e?.response?.status === 403) setDisabled(true);
      setList([]); setTotal(0);
    } finally { setLoading(false); }
  }, [q, page]);

  useEffect(() => { load(); }, [load]);

  if (settings.supporter_hall_enabled === false || disabled) {
    return (
      <PageShell>
        <section className="max-w-3xl mx-auto px-6 py-32 text-center">
          <div className="font-display text-3xl font-bold text-white">Supporter Hall</div>
          <p className="text-zinc-400 mt-3 font-mystic italic">This sanctum is currently sealed.</p>
        </section>
      </PageShell>
    );
  }

  const pages = Math.max(1, Math.ceil(total / PER_PAGE));

  return (
    <PageShell>
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="text-center mb-10">
          <div className="font-mystic text-amber-300 text-sm tracking-widest inline-flex items-center gap-2 justify-center">
            <Sparkles className="w-4 h-4" /> THE HALL OF SUPPORTERS <Sparkles className="w-4 h-4" />
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-black text-white mt-3 tracking-tighter">Supporter Hall</h1>
          <p className="text-zinc-400 mt-3 max-w-xl mx-auto font-mystic italic">
            The God Tier devotees whose tributes light the dawn.
          </p>
        </div>

        <div className="max-w-md mx-auto mb-8 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input
            value={q}
            onChange={(e) => { setQ(e.target.value); setPage(1); }}
            placeholder="Search by username…"
            className="pl-10 bg-black/40 border-white/10"
            data-testid="supporters-search"
          />
        </div>

        {loading ? (
          <div className="text-center text-zinc-500 font-mystic">Summoning supporters…</div>
        ) : list.length === 0 ? (
          <div className="text-center text-zinc-500 py-10 font-mystic italic">No supporters {q ? `matching "${q}"` : "yet"}.</div>
        ) : (
          <ul className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="supporters-grid">
            {list.map((u) => (
              <li key={u.id} className="glass rounded-xl p-5 text-center" data-testid={`supporter-${u.id}`}>
                <Avatar user={u} size={84} className="mx-auto" />
                <div className="mt-3 font-display font-bold text-white truncate">{u.name}</div>
                <div className="mt-1.5 flex justify-center">
                  <RoleBadge role={u.role} badges={u.badges} size="xs" />
                </div>
                <div className="mt-3 text-[11px] text-zinc-500">
                  Joined {new Date(u.created_at).toLocaleDateString()}
                </div>
                {u.membership_end && (
                  <div className="text-[11px] text-amber-300/80 mt-0.5">
                    Active until {new Date(u.membership_end).toLocaleDateString()}
                  </div>
                )}
                {u.total_contribution != null && (
                  <div
                    className="mt-2 inline-block px-2 py-0.5 rounded text-[11px] font-semibold bg-amber-900/30 text-amber-200 border border-amber-500/30"
                    data-testid={`supporter-${u.id}-total`}
                  >
                    {formatRp(u.total_contribution)}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}

        {pages > 1 && (
          <div className="mt-8 flex items-center justify-center gap-3">
            <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(page - 1)}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm text-zinc-400">Page {page} of {pages}</span>
            <Button variant="outline" size="sm" disabled={page === pages} onClick={() => setPage(page + 1)}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        )}
      </section>
    </PageShell>
  );
}
